import { FC, useRef } from "react";
import {
  Box,
  Chip,
  IconButton,
  Link,
  Stack,
  Tooltip,
  TypographyProps,
} from "@mui/material";
import {
  CloudOutlined,
  PlayCircleOutlined,
  OpenInNew,
} from "@mui/icons-material";
import { AccountStatusChip } from "./chips/account-status-chip/AccountStatusChip";
import { ProviderLogo } from "./provider-logo/ProviderLogo";
import { TypographyWithTooltip } from "./TypographyWithTooltip";
import { AccountAccessTypeChip } from "./chips/account-access-type-chip/AccountAccessTypeChip";
import {
  AccountStatus,
  ProviderAccessType,
  ProviderType,
} from "../../services/cloudchipr.api";
import { useHover } from "../../utils/hooks/useHover.hook";
import { getProviderName } from "../../utils/helpers/providers/getProviderName";

export interface AccountInfoDataProps extends TypographyProps {
  provider?: ProviderType;
  accountStatus?: AccountStatus;
  accessType?: ProviderAccessType;
  accountName?: string | null;
  accountId?: string;
  chipView?: boolean;
  link?: string;
  providerLink?: string;
  linkTooltipTitle?: string;
  liveUsageIcon?: boolean;
}

export const AccountInfoData: FC<AccountInfoDataProps> = ({
  accountName,
  accountStatus,
  accountId,
  provider,
  maxWidth,
  accessType,
  link,
  chipView,
  providerLink,
  liveUsageIcon,
  linkTooltipTitle,
  ...typographyProps
}) => {
  const statusChipRef = useRef<null | HTMLDivElement>(null);
  const { ref, hovered } = useHover();

  if (chipView) {
    return (
      <Chip
        variant="outlined"
        avatar={provider ? <ProviderLogo provider={provider} /> : undefined}
        sx={{ pl: 1 }}
        size="small"
        label={
          <TypographyWithTooltip
            title={accountName || accountId || ""}
            fontSize="inherit"
            maxWidth={maxWidth}
          />
        }
      />
    );
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      ref={link ? ref : undefined}
      overflow="hidden"
    >
      {provider && <ProviderLogo provider={provider} />}

      <Stack
        sx={{
          overflow: "hidden",
        }}
      >
        {accountName && (
          <TypographyWithTooltip
            title={accountName}
            maxWidth={maxWidth}
            {...typographyProps}
          />
        )}

        {accountId && (
          <TypographyWithTooltip
            title={accountId}
            color="text.disabled"
            variant="body2"
            maxWidth={maxWidth}
          />
        )}
      </Stack>

      <Stack direction="row" spacing={0.5} alignItems="center">
        {providerLink && provider && (
          <Tooltip
            title={`View in ${getProviderName(provider, {
              title: true,
            })}.`}
            arrow
            placement="top"
          >
            <IconButton
              size="small"
              component={Link}
              href={providerLink}
              sx={{
                display: hovered ? "flex" : "none",
                visibility: hovered ? "visible" : "hidden",
              }}
              target="_blank"
            >
              <CloudOutlined fontSize="small" color="action" />
            </IconButton>
          </Tooltip>
        )}

        {link && (
          <Tooltip title={linkTooltipTitle ?? ""} arrow placement="top">
            <IconButton
              size="small"
              component={Link}
              href={link}
              target="_blank"
              sx={{
                display: hovered ? "flex" : "none",
                visibility: hovered ? "visible" : "hidden",
              }}
            >
              {liveUsageIcon ? (
                <PlayCircleOutlined fontSize="small" color="action" />
              ) : (
                <OpenInNew fontSize="small" color="action" />
              )}
            </IconButton>
          </Tooltip>
        )}

        {accountStatus && (
          <Box flexGrow="1" textAlign="right" ref={statusChipRef}>
            <AccountStatusChip status={accountStatus} />
          </Box>
        )}

        {accessType && <AccountAccessTypeChip accessType={accessType} />}
      </Stack>
    </Stack>
  );
};
